import { app } from "@microsoft/teams-js";
import {
  GraphAPIPermissionScopes,
  TeamsSubscriptionPlans,
  TeamsUserPollyUseIntent,
} from "@teams/types";
import {
  GraphChatsDTO,
  GraphPermissionCosentDTO,
  GraphTeamsAndChannelsDTO,
  NpsDimissDTO,
  NpsEligibilityDTO,
  UserDTO,
} from "@teams/types/api";

import {
  FetchOptions,
  FetchResult,
  encodeApiUrl,
  fetchApiWithContext,
  fetchWithToken,
  getGroupIdFromContext,
  isSuccess,
} from "../../_shared/services/ChannelApi";

export const userApi = {
  updateUserPollyUseIntent: async (
    context: app.Context,
    params: Partial<TeamsUserPollyUseIntent>
  ): Promise<FetchResult<boolean | Error>> => {
    const result = await fetchApiWithContext(context, encodeApiUrl`/v1/user/intent`, {
      method: "POST",
      body: JSON.stringify(params),
    });

    return isSuccess(result);
  },

  getUser: async (context: app.Context): Promise<FetchResult<UserDTO>> => {
    const user: FetchResult<UserDTO> = await fetchApiWithContext(context, encodeApiUrl`/v1/user`);

    if (isSuccess(user)) {
      deserializeUserDTO(user);
    }

    return user;
  },

  getUserWithToken: async (token: string): Promise<FetchResult<UserDTO>> => {
    const url = new URL(encodeApiUrl`/v1/user`, window.location.origin);
    const user: FetchResult<UserDTO> = await fetchWithToken(url.toString(), token);

    if (isSuccess(user)) {
      deserializeUserDTO(user);
    }

    return user;
  },

  startTrialByToken: (
    token: string,
    groupId: string,
    plan: string,
    userUPN: string
  ): Promise<FetchResult<any>> => {
    return fetchWithToken(encodeApiUrl`/v1/start-trial/${plan}`, token, {
      method: "POST",
      body: JSON.stringify({
        userUPN,
        groupId,
      }),
    });
  },

  startTrial: (
    context: app.Context,
    plan: TeamsSubscriptionPlans,
    approvalContact?: string,
    licenseRequestReason?: string
  ) => {
    const groupId = getGroupIdFromContext(context);

    return fetchApiWithContext<{ error?: boolean; message?: string; code?: string }>(
      context,
      encodeApiUrl`/v1/start-trial/${plan}`,
      {
        method: "POST",
        body: JSON.stringify({
          userUPN: context.user?.userPrincipalName,
          groupId,
          approvalContact,
          licenseRequestReason,
        }),
      }
    );
  },

  getHubspotIdentityToken: (contextOrApiToken: string | app.Context) => {
    const url = encodeApiUrl`/v1/user/hubspot/identity`;

    type TokenReturn = { token: string | undefined; email: string };

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<TokenReturn>(url, contextOrApiToken);
    } else {
      return fetchApiWithContext<TokenReturn>(contextOrApiToken, url);
    }
  },

  getNpsEligibility: async (context: app.Context): Promise<FetchResult<NpsEligibilityDTO>> => {
    return await fetchApiWithContext<NpsEligibilityDTO>(
      context,
      encodeApiUrl`/v1/user/nps/eligibility`
    );
  },

  getGraphPermissionConsentStatus: async (
    context: app.Context
  ): Promise<FetchResult<GraphPermissionCosentDTO>> =>
    fetchApiWithContext<GraphPermissionCosentDTO>(context, encodeApiUrl`/v1/user/graph/consent`),

  updateUserOptOutForNps: async (
    context: app.Context,
    optOut: boolean
  ): Promise<FetchResult<void>> => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/user/nps`, {
      method: "PUT",
      body: JSON.stringify({ optOut }),
    });
  },

  updateUserGraphPermissionConsent: async (
    context: app.Context,
    consented: GraphAPIPermissionScopes[]
  ) =>
    fetchApiWithContext<void>(context, encodeApiUrl`/v1/user/graph/consent`, {
      method: "PUT",
      body: JSON.stringify({ consented }),
    }),

  updateUserDismissedAtForNps: async (context: app.Context): Promise<FetchResult<NpsDimissDTO>> => {
    return fetchApiWithContext<NpsDimissDTO>(context, encodeApiUrl`/v1/user/nps`, {
      method: "PUT",
      body: JSON.stringify({ dimiss: true }),
    });
  },

  updateUserSubmittedAtForNps: async (context: app.Context): Promise<FetchResult<void>> => {
    return fetchApiWithContext<void>(context, encodeApiUrl`/v1/user/nps`, {
      method: "PUT",
      body: JSON.stringify({ submitted: true }),
    });
  },

  getSurveyCountByOwner: async (context: app.Context) => {
    return await fetchApiWithContext<Number>(context, encodeApiUrl`/v1/user/survey-count`);
  },

  getChannelList: async (contextOrApiToken: string | app.Context) => {
    const url = encodeApiUrl`/v1/user/ms-graph/channels`;

    const params = new URLSearchParams();

    const options: FetchOptions = {
      method: "GET",
      queryParams: params,
    };

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<Promise<GraphTeamsAndChannelsDTO>>(url, contextOrApiToken, options);
    } else {
      return fetchApiWithContext<Promise<GraphTeamsAndChannelsDTO>>(
        contextOrApiToken,
        url,
        options
      );
    }
  },

  getChatList: async (contextOrApiToken: string | app.Context) => {
    const url = encodeApiUrl`/v1/user/ms-graph/chats`;

    const params = new URLSearchParams();

    const options: FetchOptions = {
      method: "GET",
      queryParams: params,
    };

    if (typeof contextOrApiToken === "string") {
      return fetchWithToken<Promise<GraphChatsDTO>>(url, contextOrApiToken, options);
    } else {
      return fetchApiWithContext<Promise<GraphChatsDTO>>(contextOrApiToken, url, options);
    }
  },
};

export const deserializeUserDTO = (user: UserDTO): void => {
  user.subscriptions.forEach(subscription => {
    subscription.createdAt = new Date(subscription.createdAt);
    subscription.updatedAt = new Date(subscription.updatedAt);
    if (subscription.trialExpiresAt) {
      subscription.trialExpiresAt = new Date(subscription.trialExpiresAt);
    }
  });
};
